import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/**
 * @typedef {Object} ExcelColumn
 * @property {string} header - Column header shown in the sheet
 * @property {string} [key] - Record property (used when getValue is omitted)
 * @property {number} [width] - Approximate column width in characters
 * @property {(record: object, index: number) => any} [getValue] - Custom cell value resolver
 */

/**
 * @typedef {Object} ExcelSummaryRow
 * @property {string} label
 * @property {string|number} value
 */

const resolveCellValue = (record, index, column) => {
    if (typeof column.getValue === "function") {
        return column.getValue(record, index);
    }
    if (column.key) {
        const value = record[column.key];
        return value ?? "";
    }
    return "";
};

/**
 * Export records to an Excel file with ordered columns and optional summary metadata.
 *
 * @param {Object} options
 * @param {Array<object>} options.records
 * @param {ExcelColumn[]} options.columns
 * @param {string} [options.fileName]
 * @param {string} [options.sheetName]
 * @param {ExcelSummaryRow[]} [options.summaryRows]
 */
export const exportRecordsToExcel = ({
    records,
    columns,
    fileName = "export.xlsx",
    sheetName = "Data",
    summaryRows = [],
}) => {
    if (!records?.length) {
        console.warn("No records found to export");
        return false;
    }

    if (!columns?.length) {
        console.warn("No columns configured for export");
        return false;
    }

    const worksheet = XLSX.utils.aoa_to_sheet([]);
    let nextRow = 0;

    if (summaryRows.length > 0) {
        XLSX.utils.sheet_add_aoa(
            worksheet,
            summaryRows.map((row) => [row.label, row.value]),
            { origin: { r: nextRow, c: 0 } }
        );
        nextRow += summaryRows.length + 1;
    }

    const headers = columns.map((column) => column.header);
    const dataRows = records.map((record, index) =>
        columns.map((column) => resolveCellValue(record, index, column))
    );

    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: { r: nextRow, c: 0 } });
    XLSX.utils.sheet_add_aoa(worksheet, dataRows, { origin: { r: nextRow + 1, c: 0 } });

    worksheet["!cols"] = columns.map((column) => ({ wch: column.width || 18 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, fileName);
    return true;
};

/**
 * Legacy helper — maps object keys to readable headers and exports as JSON rows.
 */
export const exportToExcel = (records, labels, fileName = "products.xlsx", sheetName = "Products") => {
    if (!records || records.length === 0) {
        console.warn("No records found to export");
        return false;
    }

    const columns = Object.keys(records[0]).map((key) => {
        let header;
        if (/[^a-zA-Z0-9_]/.test(key)) {
            header = key;
        } else {
            const keyTitle = key
                ? key
                      .replace(/_/g, " ")
                      .toLowerCase()
                      .split(" ")
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(" ")
                : "";
            header = labels?.[key]?.title || keyTitle || key;
        }

        return {
            header,
            getValue: (record) => record[key],
        };
    });

    return exportRecordsToExcel({ records, columns, fileName, sheetName });
};
